import { Message, MessageLogic } from '@/modules/message';
import type { MessageIntent } from '@/modules/template/types';
import type {
  AllowedDetailSettings,
  BylineChoice,
  BylineNames,
  Topic,
  TopicDiffSet,
  TopicFieldDiff,
  TopicOtherDiffKey,
  TopicTypeId,
} from './types';
import {
  DEFAULT_STARTING_SENDER_APPOSITIVE,
  TOPIC_TYPES,
  type ReshareStorySettings,
} from './types';
import { normalizeTimezone } from './timezone';
import { TopicSchema } from './schema';
import { PLACE_TIMEZONES } from '@/modules/config/placeTimezones';
import {
  datetimeAsDateObj,
  formatTopicDate,
  formatTopicDateTimeLines,
  formatTopicDatetime,
  formatTopicTime,
  startOfDayInZone,
  formatMultiDayTopicRange,
  formatSameDayTopicRange,
} from './datetime';

function typeLabel(type: TopicTypeId): string {
  return TOPIC_TYPES.find(t => t.id === type)?.choiceLabel ?? type;
}

// Human-readable one-line summary of the topic's date/time range in its own
// authored timezone (with the timezone abbreviation appended).
function datetimeText(topic: Topic): string {
  try {
    return formatTopicDateTimeLines(
      topic,
      normalizeTimezone(topic.timezone),
      true
    ).join(' · ');
  } catch {
    return '';
  }
}

// Whether two optional timestamps name the same instant, however each is
// formatted ("…:00Z" and "…:00.000Z" match).
function sameInstant(a: string | undefined, b: string | undefined): boolean {
  if (a === undefined || b === undefined) {
    return a === b;
  }
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isNaN(ta) || Number.isNaN(tb) ? a === b : ta === tb;
}

// JSON with every object's keys sorted, so key order never reads as a change.
function canonicalJson(value: unknown): string {
  return JSON.stringify(value ?? null, (_key, v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>).sort(([a], [b]) =>
            a < b ? -1 : a > b ? 1 : 0
          )
        )
      : v
  );
}

/**
 * TopicLogic - Service functions for Topic POJO operations
 */
export const TopicLogic = {
  /**
   * Creates an Topic POJO from API response data, validating via Zod.
   */
  fromJSON(data: unknown): Topic {
    return TopicSchema.parse(data) as Topic;
  },

  /**
   * The trimmed body cut to at most `maxChars` characters. A longer body keeps
   * its first `maxChars - 3` characters and ends in "...", so the excerpt is
   * exactly `maxChars` long. Counted in code points, so an emoji is never
   * split.
   */
  bodyExcerpt(body: string, maxChars: number): string {
    const text = body.trim();
    const chars = Array.from(text);
    return chars.length <= maxChars
      ? text
      : `${chars.slice(0, maxChars - 3).join('')}...`;
  },

  /**
   * Creates the message body for sharing this topic.
   * This is the portion used for duplicate checking.
   */
  getShareBody(topic: Topic): string {
    // URL encode the topic ID for use in URLs (base64 UUID80 may contain +, /, = characters)
    const encodedTopicId = encodeURIComponent(topic.id);
    const topicUrl = `${window.location.origin}/topics/${encodedTopicId}`;
    const bodyWithPeriod = topic.body.endsWith('.')
      ? topic.body
      : topic.body + '.';
    return `${topic.title.toUpperCase()}-- ${bodyWithPeriod} Register at ${topicUrl}`;
  },

  /**
   * Creates a Message instance for sharing this topic.
   *
   * @param topic - The topic to share
   * @returns A Message instance with body
   */
  toMessage(topic: Topic): Message {
    return MessageLogic.create(TopicLogic.getShareBody(topic));
  },

  /**
   * The topic's impression goal (#447) clamped to the global ceiling. Clamping
   * happens at every read so a lowered ceiling applies immediately to topics
   * that already have a goal. null means "no goal".
   *
   * Note the asymmetry with effectiveIntroLimit: there, either ceiling can
   * supply a limit. Here the ceiling only ever clamps a goal the topic actually
   * set — it never creates one. A non-finite or non-positive goal is no goal,
   * so this is safe to call on a parsed form field.
   */
  effectiveImpressionGoal(
    perTopic?: number | null,
    impressionGoalMax?: number | null
  ): number | null {
    if (typeof perTopic !== 'number' || !Number.isFinite(perTopic)) return null;
    if (perTopic < 1) return null;
    if (
      typeof impressionGoalMax !== 'number' ||
      !Number.isFinite(impressionGoalMax) ||
      impressionGoalMax < 0
    ) {
      return perTopic;
    }
    return Math.min(perTopic, impressionGoalMax);
  },

  /**
   * True when the topic has reached (or passed) its effective impression goal.
   * False when there is no goal. Reaching it is a milestone, never a limit.
   */
  impressionGoalReached(
    impressionCount: number | null | undefined,
    perTopic?: number | null,
    impressionGoalMax?: number | null
  ): boolean {
    const goal = TopicLogic.effectiveImpressionGoal(
      perTopic,
      impressionGoalMax
    );
    return goal != null && (impressionCount ?? 0) >= goal;
  },

  /**
   * The field id of the slot in `intent`'s form the allowed detail comes from,
   * or null when that form has none: the Settings tab names an intro slot.
   * Only an intro carries an allowed detail.
   */
  allowedDetailSlotId(
    settings: AllowedDetailSettings | null | undefined,
    intent: MessageIntent
  ): string | null {
    if (intent !== 'intro') return null;
    return settings?.slotId || null;
  },

  /**
   * Whether a share of this topic type opens with the exclude-aware box ticked
   * (#581) — the type's own opening position, declared on TOPIC_TYPES. The
   * sharing entry points seed the wizard session with it, so the wizard itself
   * reads the session and knows nothing about topic types. Unknown types, and
   * types that say nothing, open unticked.
   */
  defaultsToExcludingAwareRecipients(type: string | undefined): boolean {
    return (
      TOPIC_TYPES.find(t => t.id === type)
        ?.defaultsToExcludingAwareRecipients ?? false
    );
  },

  /**
   * Whether a topic of this type has an end time, as declared on TOPIC_TYPES.
   * Unknown types, and types that say nothing, have one.
   */
  hasEndTime(type: string | undefined): boolean {
    return !TOPIC_TYPES.find(t => t.id === type)?.hasNoEndTime;
  },

  /**
   * How the reshare story describes the starting sender of a topic of this
   * type until its Settings tab picks a description — the type's own, declared
   * on TOPIC_TYPES, or DEFAULT_STARTING_SENDER_APPOSITIVE.
   */
  defaultStartingSenderAppositive(type: string | undefined): string {
    return (
      TOPIC_TYPES.find(t => t.id === type)?.startingSenderAppositive ??
      DEFAULT_STARTING_SENDER_APPOSITIVE
    );
  },

  /**
   * The settings a story of this topic is told by: what its Settings tab
   * saved, with the type's starting sender description filled in where the
   * tab picked none.
   */
  storySharingSettings(topic: Topic): ReshareStorySettings {
    const settings = topic.reshareStorySettings ?? {};
    return {
      ...settings,
      startingSenderAppositive:
        settings.startingSenderAppositive ??
        TopicLogic.defaultStartingSenderAppositive(topic.type),
    };
  },

  /** The topic's start as a Date: the instant it starts. */
  startTimeAsDateObj(topic: Topic): Date | undefined {
    return datetimeAsDateObj(topic.startTime);
  },

  /** The topic's end as a Date, or undefined for a topic with no end (news). */
  endTimeAsDateObj(topic: Topic): Date | undefined {
    return datetimeAsDateObj(topic.endTime);
  },

  /** The day the topic starts, as a Date at midnight in the topic's zone. */
  startDateAsDateObj(topic: Topic): Date | undefined {
    return startOfDayInZone(
      TopicLogic.startTimeAsDateObj(topic),
      topic.timezone
    );
  },

  /**
   * The day the topic ends, as a Date at midnight in the topic's zone, or null
   * for a topic with no end (news).
   */
  endDateAsDateObj(topic: Topic): Date | undefined {
    return startOfDayInZone(TopicLogic.endTimeAsDateObj(topic), topic.timezone);
  },

  formatTopicDate,
  formatTopicTime,
  formatTopicDatetime,
  formatSameDayTopicRange,
  formatMultiDayTopicRange,
  formatTopicDateTimeLines,

  /**
   * Which of the author's attribution names a byline is: `familiar` or
   * `allowed` when it is that name, `none` when empty, and `custom` otherwise.
   * The names are compared as given: UserContext already provides them trimmed.
   */
  bylineChoiceFor(byline: string, names: BylineNames): BylineChoice {
    const text = byline.trim();
    if (text === '') return 'none';
    if (text === names.familiarName) return 'familiar';
    if (text === names.allowedName) return 'allowed';
    return 'custom';
  },

  /**
   * The IANA zone a location is in, when it names a place
   * PLACE_TIMEZONES knows; otherwise undefined. Case, accents and punctuation are
   * ignored, and "Portland, Maine" is tried whole before its city alone.
   */
  timezoneForPlace(place: string): string | undefined {
    const normalize = (text: string) =>
      text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    const whole = normalize(place);
    const city = normalize(place.split(',')[0] ?? '');
    return PLACE_TIMEZONES[whole] ?? PLACE_TIMEZONES[city];
  },
  /**
   * The core, recipient-facing changes between two versions of a topic — title,
   * type, date/time, location, body and image — with friendly before/after values for the
   * change log. Null when none of them changed.
   */
  computeCoreDiffs(before: Topic, after: Topic): TopicDiffSet | null {
    const fields: TopicFieldDiff[] = [];

    if (before.title !== after.title) {
      fields.push({
        key: 'title',
        label: 'Title',
        before: before.title,
        after: after.title,
      });
    }

    if (before.type !== after.type) {
      fields.push({
        key: 'type',
        label: 'Type',
        before: typeLabel(before.type),
        after: typeLabel(after.type),
      });
    }

    // Compare the instants and zone (robust to formatting), but show the
    // friendly formatted range to the manager.
    const datetimeChanged =
      !sameInstant(before.startTime, after.startTime) ||
      !sameInstant(before.endTime, after.endTime) ||
      normalizeTimezone(before.timezone) !== normalizeTimezone(after.timezone);
    if (datetimeChanged) {
      fields.push({
        key: 'datetime',
        label: 'When',
        before: datetimeText(before),
        after: datetimeText(after),
      });
    }

    // Absent and empty read alike.
    const beforeLocation = before.location ?? '';
    const afterLocation = after.location ?? '';
    if (beforeLocation !== afterLocation) {
      fields.push({
        key: 'location',
        label: 'Location',
        before: beforeLocation,
        after: afterLocation,
      });
    }

    if (before.body !== after.body) {
      fields.push({
        key: 'body',
        label: 'Body',
        before: before.body,
        after: after.body,
      });
    }

    const imageChanged = before.imageData !== after.imageData;

    if (fields.length === 0 && !imageChanged) {
      return null;
    }

    return { fields, imageChanged };
  },

  /**
   * The saved settings that differ between two versions of a topic, beyond the
   * core fields computeCoreDiffs reports: sharing limits, the sharing setup
   * (custom form and reshare story settings), the impression goal, and the
   * byline. Absent and empty read alike. Empty when none changed.
   */
  computeOtherDiffs(before: Topic, after: Topic): TopicOtherDiffKey[] {
    const comparable = (topic: Topic): Record<TopicOtherDiffKey, unknown> => ({
      introCountLimit: topic.introCountLimit ?? null,
      onlyManagersCanShare: topic.onlyManagersCanShare ?? false,
      byInviteOnly: topic.byInviteOnly ?? false,
      customTemplates: topic.customTemplates || null,
      reshareStorySettings: canonicalJson(topic.reshareStorySettings),
      impressionGoal: topic.impressionGoal ?? null,
      byline: topic.byline ?? '',
    });
    const a = comparable(before);
    const b = comparable(after);
    return (Object.keys(a) as TopicOtherDiffKey[]).filter(
      key => a[key] !== b[key]
    );
  },
};
